import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import promptService, { Prompt, PromptSection, PromptDeployment, PromptProvider, PromptThinkingLevel, PromptCriteria, MODEL_OPTIONS } from '../services/promptService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { summaryTypeService, SummaryType } from '../services/summaryTypeService';
import { AvailableVariable } from '../services/promptService';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import RequirePermission from '../components/common/RequirePermission';
import CriteriaEditorPanel from '../components/criteria/CriteriaEditorPanel';
import { NotificationEntity } from '../services/patientNotificationService';
import { userService } from '../services/userService';
import { clinicService } from '../services/clinicService';
import patientStatusConfigService from '../services/patientStatusConfigService';
import { useTenant } from '../context/TenantContext';
import { useUserTimezone } from '../hooks/useUserTimezone';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { promptColumns } from '../config/promptColumns';

const emptyCriteria = (): PromptCriteria => ({ include: [], exclude: [] });


const ltrTheme = createTheme({ direction: 'ltr' });

const PromptManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userTimezone = useUserTimezone();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editTab, setEditTab] = useState(0);
  const [copyTab, setCopyTab] = useState(0);
  const [addTab, setAddTab] = useState(0);
  const [editSectionTab, setEditSectionTab] = useState(0);
  const [copySectionTab, setCopySectionTab] = useState(0);
  const [addSectionTab, setAddSectionTab] = useState(0);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [summaryTypes, setSummaryTypes] = useState<SummaryType[]>([]);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, { id: number | string; name: string }[]>>({});
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    general: string;
    sections: PromptSection[];
    criteria: PromptCriteria;
    deployment: PromptDeployment;
    priority: number;
    model_provider: PromptProvider | null;
    model: string | null;
    thinking_level: PromptThinkingLevel | null;
    meeting_type_id: number | null;
    summary_type_id: number | null;
  }>({
    name: '',
    general: '',
    sections: [],
    criteria: emptyCriteria(),
    deployment: 'dev',
    priority: 1,
    model_provider: null,
    model: null,
    thinking_level: null,
    meeting_type_id: null,
    summary_type_id: null,
  });
  const [addFormData, setAddFormData] = useState<{
    name: string;
    prompt_type: string;
    meeting_type_id: number | '';
    summary_type_id: number | null;
    general: string;
    sections: PromptSection[];
    criteria: PromptCriteria;
    is_global: boolean;
    is_active: boolean;
    deployment: PromptDeployment;
    priority: number;
    model_provider: PromptProvider | null;
    model: string | null;
    thinking_level: PromptThinkingLevel | null;
  }>({
    name: '',
    prompt_type: '',
    meeting_type_id: '',
    summary_type_id: null,
    general: '',
    sections: [],
    criteria: emptyCriteria(),
    is_global: false,
    is_active: true,
    deployment: 'dev',
    priority: 1,
    model_provider: null,
    model: null,
    thinking_level: null,
  });

  const [availableVariables, setAvailableVariables] = useState<AvailableVariable[]>([]);
  const generalTextRef = useRef<HTMLTextAreaElement>(null);

  const { user: currentUser } = useAuth();

  const loadVariables = async (promptType: string) => {
    if (!promptType) { setAvailableVariables([]); return; }
    try {
      const vars = await promptService.getPromptVariables(promptType);
      setAvailableVariables(vars);
    } catch {
      setAvailableVariables([]);
    }
  };

  const insertVariable = (varName: string, isAdd: boolean) => {
    const textarea = generalTextRef.current;
    const token = `{${varName}}`;
    if (textarea) {
      const start = textarea.selectionStart ?? textarea.value.length;
      const end = textarea.selectionEnd ?? textarea.value.length;
      const before = textarea.value.substring(0, start);
      const after = textarea.value.substring(end);
      const newValue = before + token + after;
      if (isAdd) {
        setAddFormData(prev => ({ ...prev, general: newValue }));
      } else {
        setFormData(prev => ({ ...prev, general: newValue }));
      }
      setTimeout(() => {
        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    } else {
      if (isAdd) {
        setAddFormData(prev => ({ ...prev, general: prev.general + token }));
      } else {
        setFormData(prev => ({ ...prev, general: prev.general + token }));
      }
    }
  };

  const getSortValue = useCallback((prompt: Prompt, key: string): any => {
    switch (key) {
      case 'name': return prompt.name.toLowerCase();
      case 'type': return prompt.prompt_type.toLowerCase();
      case 'sections': return prompt.sections?.length || 0;
      case 'deployment': return prompt.deployment;
      case 'priority': return prompt.priority;
      case 'modelProvider': return prompt.model_provider || '';
      case 'model': return prompt.model || '';
      case 'thinkingLevel': return prompt.thinking_level || '';
      case 'createdAt': return prompt.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((prompt: Prompt): string[] => {
    return [prompt.name, prompt.prompt_type, prompt.tenant_name || ''];
  }, []);

  const table = useTablePage<Prompt>({
    entityType: 'prompts',
    columns: promptColumns,
    getSortValue,
    getSearchText,
  });

  useEffect(() => {
    fetchPrompts();
    meetingTypeService.getMeetingTypes().then(setMeetingTypes).catch(() => {});
    summaryTypeService.getSummaryTypes().then(setSummaryTypes).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!currentTenant) return;
    Promise.all([
      meetingTypeService.getMeetingTypes(currentTenant.id),
      userService.getUsers(currentTenant.id),
      patientStatusConfigService.getAll(currentTenant.id),
      clinicService.getClinics(currentTenant.id),
    ]).then(([mts, users, statuses, clinics]) => {
      setDynamicOptions({
        meetingTypes: mts.map((t) => ({ id: t.id, name: t.name })),
        users: users.map((u) => ({ id: u.id, name: u.full_name })),
        patientStatuses: statuses.map((s) => ({ id: s.key, name: s.title })),
        clinics: clinics.map((c) => ({ id: c.id, name: c.name })),
      });
    }).catch(() => {});
  }, [currentTenant]);

  const fetchPrompts = () => {
    table.loadData(() => promptService.getPrompts(undefined, undefined), 'Failed to load prompts');
  };

  const sectionsFromSummaryType = (stId: number | null | undefined): PromptSection[] => {
    if (!stId) return [];
    const st = summaryTypes.find((s) => s.id === stId);
    return (st?.summary_sections ?? [])
      .filter((s: any) => s.type === 'text')
      .map((s: any) => ({ name: s.name, title: s.label || s.name, prompt: '' }));
  };

  const getSectionLabel = (section: PromptSection, stId: number | null | undefined, index: number): string => {
    if (stId) {
      const st = summaryTypes.find((s) => s.id === stId);
      const match = st?.summary_sections?.find((ss) => ss.name === (section.name || section.title));
      if (match?.label) return match.label;
    }
    return section.title || `Section ${index + 1}`;
  };

  const handleEdit = () => {
    if (!table.selectedItem) return;
    setSelectedPrompt(table.selectedItem);
    setFormData({
      name: table.selectedItem.name,
      general: table.selectedItem.general || '',
      sections: table.selectedItem.sections || [],
      criteria: table.selectedItem.criteria ?? emptyCriteria(),
      deployment: table.selectedItem.deployment ?? 'dev',
      priority: table.selectedItem.priority ?? 1,
      model_provider: table.selectedItem.model_provider ?? null,
      model: table.selectedItem.model ?? null,
      thinking_level: table.selectedItem.thinking_level ?? null,
      meeting_type_id: table.selectedItem.meeting_type_id ?? null,
      summary_type_id: table.selectedItem.summary_type_id ?? null,
    });
    loadVariables(table.selectedItem.prompt_type);
    setEditTab(0);
    setEditSectionTab(0);
    setEditDialogOpen(true);
    table.handleMenuClose();
  };

  const handleOpenCopyDialog = () => {
    if (!table.selectedItem) return;
    setSelectedPrompt(table.selectedItem);
    setFormData({
      name: table.selectedItem.name,
      general: table.selectedItem.general || '',
      sections: table.selectedItem.sections || [],
      criteria: table.selectedItem.criteria ?? emptyCriteria(),
      deployment: table.selectedItem.deployment ?? 'dev',
      priority: table.selectedItem.priority ?? 1,
      model_provider: table.selectedItem.model_provider ?? null,
      model: table.selectedItem.model ?? null,
      thinking_level: table.selectedItem.thinking_level ?? null,
      meeting_type_id: table.selectedItem.meeting_type_id ?? null,
      summary_type_id: table.selectedItem.summary_type_id ?? null,
    });
    loadVariables(table.selectedItem.prompt_type);
    setCopyTab(0);
    setCopySectionTab(0);
    setCopyDialogOpen(true);
    table.handleMenuClose();
  };

  const handleCopyToTenant = async () => {
    if (!table.selectedItem || !currentUser?.tenant_id) {
      table.setError('You must belong to a tenant to copy prompts');
      return;
    }
    try {
      await promptService.copyToTenant(table.selectedItem.id, currentUser.tenant_id);
      table.setError(null);
      fetchPrompts();
      alert('Prompt copied successfully!');
    } catch (err: any) {
      table.setError(err.response?.data?.detail || 'Failed to copy prompt');
    }
    table.handleMenuClose();
  };

  const handleSaveCopy = async () => {
    if (!selectedPrompt || !currentUser?.tenant_id) {
      table.setError('No tenant available');
      return;
    }

    try {
      await promptService.createPrompt({
        tenant_id: currentUser.tenant_id,
        prompt_type: selectedPrompt.prompt_type,
        name: formData.name,
        general: formData.general,
        sections: formData.sections,
        criteria: formData.criteria,
        is_global: false,
        is_active: true,
        summary_type_id: formData.summary_type_id,
      });
      setCopyDialogOpen(false);
      setSelectedPrompt(null);
      fetchPrompts();
      table.setError(null);
      alert('Tenant-specific prompt created successfully!');
    } catch (err: any) {
      table.setError(err.response?.data?.detail || 'Failed to create prompt');
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt) return;

    try {
      await promptService.updatePrompt(selectedPrompt.id, {
        name: formData.name,
        general: formData.general,
        sections: formData.sections,
        criteria: formData.criteria,
        deployment: formData.deployment,
        priority: formData.priority,
        model_provider: formData.model_provider,
        model: formData.model,
        thinking_level: formData.thinking_level,
        meeting_type_id: formData.meeting_type_id,
        summary_type_id: formData.summary_type_id,
      });
      setEditDialogOpen(false);
      setSelectedPrompt(null);
      fetchPrompts();
      table.setError(null);
    } catch (err: any) {
      table.setError(err.response?.data?.detail || 'Failed to save prompt');
    }
  };

  const handleDelete = async () => {
    if (!table.selectedItem || !window.confirm(`Are you sure you want to delete "${table.selectedItem.name}"?`)) {
      table.handleMenuClose();
      return;
    }
    try {
      await promptService.deletePrompt(table.selectedItem.id);
      fetchPrompts();
      table.setError(null);
    } catch (err: any) {
      table.setError(err.response?.data?.detail || 'Failed to delete prompt');
    }
    table.handleMenuClose();
  };

  const handleAddSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, { title: '', prompt: '' }],
    });
  };

  const handleRemoveSection = (index: number) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData({ ...formData, sections: newSections });
  };

  const handleSectionChange = (index: number, field: 'title' | 'prompt', value: string) => {
    const newSections = [...formData.sections];
    newSections[index][field] = value;
    setFormData({ ...formData, sections: newSections });
  };

  const handleOpenAddDialog = () => {
    setAddFormData({ name: '', prompt_type: '', meeting_type_id: '', summary_type_id: null, general: '', sections: [], criteria: emptyCriteria(), is_global: false, is_active: true, deployment: 'dev', priority: 1, model_provider: null, model: null, thinking_level: null });
    setAvailableVariables([]);
    setAddTab(0);
    setAddSectionTab(0);
    setAddDialogOpen(true);
  };

  const handleDuplicate = () => {
    if (!table.selectedItem) return;
    const src = table.selectedItem;
    setAddFormData({
      name: `Copy of ${src.name}`,
      prompt_type: src.prompt_type,
      meeting_type_id: src.meeting_type_id ?? '',
      summary_type_id: src.summary_type_id ?? null,
      general: src.general || '',
      sections: src.sections ? src.sections.map(s => ({ ...s })) : [],
      criteria: src.criteria ? { include: [...(src.criteria.include || [])], exclude: [...(src.criteria.exclude || [])] } : emptyCriteria(),
      is_global: src.is_global,
      is_active: src.is_active,
      deployment: src.deployment ?? 'dev',
      priority: src.priority ?? 1,
      model_provider: src.model_provider ?? null,
      model: src.model ?? null,
      thinking_level: src.thinking_level ?? null,
    });
    loadVariables(src.prompt_type);
    setAddTab(0);
    setAddSectionTab(0);
    setAddDialogOpen(true);
    table.handleMenuClose();
  };

  const handleMeetingTypeSelect = (meetingTypeId: number | '') => {
    if (meetingTypeId === '') {
      setAddFormData((prev) => ({ ...prev, meeting_type_id: '', sections: [] }));
      return;
    }
    const mt = meetingTypes.find((m) => m.id === meetingTypeId);
    const sections: PromptSection[] = mt?.summary_type?.summary_sections?.map((s: any) => ({ name: s.name, title: s.label || s.name, prompt: '' })) ?? [];
    setAddFormData((prev) => ({ ...prev, meeting_type_id: meetingTypeId, sections }));
  };

  const handleSummaryTypeSelect = (stId: number | null, isAdd: boolean) => {
    const sections = stId ? sectionsFromSummaryType(stId) : [];
    if (isAdd) {
      setAddFormData((prev) => ({ ...prev, summary_type_id: stId, sections }));
    } else {
      setFormData((prev) => ({ ...prev, summary_type_id: stId, sections }));
    }
  };

  const handleAddPrompt = async () => {
    if (!addFormData.name.trim() || !addFormData.prompt_type.trim()) {
      table.setError('Name and Prompt Type are required');
      return;
    }
    try {
      await promptService.createPrompt({
        tenant_id: addFormData.is_global ? null : currentUser?.tenant_id,
        prompt_type: addFormData.prompt_type,
        name: addFormData.name,
        general: addFormData.general || null,
        sections: addFormData.sections.length ? addFormData.sections : null,
        criteria: (addFormData.criteria.include.length || addFormData.criteria.exclude.length) ? addFormData.criteria : null,
        is_global: addFormData.is_global,
        is_active: addFormData.is_active,
        deployment: addFormData.deployment,
        priority: addFormData.priority,
        model_provider: addFormData.model_provider,
        model: addFormData.model,
        thinking_level: addFormData.thinking_level,
        summary_type_id: addFormData.summary_type_id,
      });
      setAddDialogOpen(false);
      fetchPrompts();
      table.setError(null);
    } catch (err: any) {
      table.setError(err.response?.data?.detail || 'Failed to create prompt');
    }
  };

  const handleAddSectionForAdd = () => {
    setAddFormData({ ...addFormData, sections: [...addFormData.sections, { title: '', prompt: '' }] });
  };

  const handleRemoveSectionForAdd = (index: number) => {
    setAddFormData({ ...addFormData, sections: addFormData.sections.filter((_, i) => i !== index) });
  };

  const handleSectionChangeForAdd = (index: number, field: 'title' | 'prompt', value: string) => {
    const newSections = [...addFormData.sections];
    newSections[index][field] = value;
    setAddFormData({ ...addFormData, sections: newSections });
  };

  const getCriteriaEntity = (promptType: string): NotificationEntity | null => {
    if (promptType === 'questionnaire_summary') return NotificationEntity.PATIENT;
    if (promptType === 'meeting_summary') return NotificationEntity.MEETING;
    return null;
  };

  const canEdit = (prompt: Prompt) => {
    if (prompt.is_global) {
      return currentUser?.tenant_id === 0; // global prompts are superadmin-only
    }
    return true; // Can edit tenant-specific prompts
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    const options: ContextMenuOption[] = [];

    if (canEdit(table.selectedItem)) {
      options.push({ label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit });
    }
    options.push({ label: 'Duplicate', icon: <CopyIcon sx={{ mr: 2 }} />, onClick: handleDuplicate });
    if (table.selectedItem.is_global && currentUser?.tenant_id === 0) {
      options.push({ label: 'Create Tenant Copy', icon: <ContentCopyIcon sx={{ mr: 2 }} />, onClick: handleOpenCopyDialog });
    }
    if (table.selectedItem.is_global && currentUser?.tenant_id) {
      options.push({ label: 'Quick Copy to My Tenant', icon: <CopyIcon sx={{ mr: 2 }} />, onClick: handleCopyToTenant });
    }
    if (canEdit(table.selectedItem)) {
      options.push({ label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const });
    }
    return options;
  };

  // Column value renderer
  const getColumnValue = (prompt: Prompt, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{prompt.name}</Typography>
          </Box>
        );
      case 'type':
        return <Chip label={prompt.prompt_type} size="small" variant="outlined" />;
      case 'sections':
        return prompt.sections?.length || 0;
      case 'deployment':
        return <Chip label={prompt.deployment} size="small" color={prompt.deployment === 'prod' ? 'error' : prompt.deployment === 'pilot' ? 'warning' : 'default'} variant="outlined" />;
      case 'priority':
        return prompt.priority;
      case 'modelProvider':
        return prompt.model_provider ? <Chip label={prompt.model_provider} size="small" variant="outlined" /> : '—';
      case 'model':
        return prompt.model ? <Chip label={prompt.model} size="small" variant="outlined" /> : '—';
      case 'thinkingLevel':
        return prompt.thinking_level ? <Chip label={prompt.thinking_level} size="small" variant="outlined" /> : '—';
      case 'createdAt':
        return formatDate(prompt.created_at, userTimezone);
      default: return '';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (prompt: Prompt) => (
    <>
      <Box display="flex" alignItems="center" mb={1} sx={{ pr: 5 }}>
        <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
          {prompt.name}
        </Typography>
      </Box>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Chip label={prompt.prompt_type} size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {prompt.sections?.length || 0} sections
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(prompt.created_at, userTimezone)}
        </Typography>
      </Box>
    </>
  );

  return (
  <RequirePermission check={!!user?.user_privilege?.config} featureName="prompt management">
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading prompts:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Prompt Management"
        searchPlaceholder="Search prompts by name or type..."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog} size="small">
            Add Prompt
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(p) => p.id}
          emptyIcon={<DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No prompts found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(p) => p.id}
            onRowClick={table.handleRowClick}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Edit Dialog */}
      <ClinicDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit Prompt"
        maxWidth="md"
        fullHeight
        actions={
          <>
            <Button onClick={handleSave} variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              Save
            </Button>
            <Button onClick={() => setEditDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>
              Cancel
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} sx={{ mb: 2 }}>
          <Tab label="Settings" />
          <Tab label="Prompt" />
          <Tab label="Sections" />
          {getCriteriaEntity(selectedPrompt?.prompt_type ?? '') && <Tab label="Criteria" />}
        </Tabs>

        {editTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField fullWidth size="small" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
            </Box>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Prompt Type</Typography>
              <Typography variant="body2" sx={{ py: '6px' }}>{selectedPrompt?.prompt_type || '—'}</Typography>
            </Box>
            {selectedPrompt?.prompt_type === 'meeting_summary' && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Meeting Type</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.meeting_type_id ?? ''} onChange={(e) => setFormData({ ...formData, meeting_type_id: (e.target.value || null) as number | null })} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {meetingTypes.map((mt) => <MenuItem key={mt.id} value={mt.id}>{mt.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}
            {selectedPrompt?.prompt_type === 'meeting_summary' && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Summary Type</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.summary_type_id ?? ''} onChange={(e) => handleSummaryTypeSelect((e.target.value || null) as number | null, false)} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {summaryTypes.map((st) => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Deployment</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.deployment} onChange={(e) => setFormData({ ...formData, deployment: e.target.value as PromptDeployment })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value="dev">Dev</MenuItem>
                    <MenuItem value="pilot">Pilot</MenuItem>
                    <MenuItem value="prod">Prod</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1, minWidth: 80 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Priority</Typography>
                <TextField fullWidth size="small" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Provider</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.model_provider || ''} onChange={(e) => setFormData({ ...formData, model_provider: (e.target.value || null) as PromptProvider | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 2, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Model</Typography>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={MODEL_OPTIONS[formData.model_provider || ''] || []}
                  value={formData.model || ''}
                  onInputChange={(_, value) => setFormData({ ...formData, model: value || null })}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="e.g. gpt-4o, claude-opus-4-7" InputProps={{ ...params.InputProps, style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Thinking Level</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.thinking_level || ''} onChange={(e) => setFormData({ ...formData, thinking_level: (e.target.value || null) as PromptThinkingLevel | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        )}

        {editTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, minHeight: 0 }}>
            {availableVariables.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Click to insert variable at cursor:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableVariables.map((v) => (
                    <Chip key={v.name} label={`{${v.name}}`} size="small" variant="outlined" onClick={() => insertVariable(v.name, false)} sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              value={formData.general}
              onChange={(e) => setFormData({ ...formData, general: e.target.value })}
              multiline
              placeholder="Enter the general prompt text..."
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties, inputRef: generalTextRef }}
              sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
            />
          </Box>
        )}

        {editTab === 2 && (() => {
          const isMeetingSummary = selectedPrompt?.prompt_type === 'meeting_summary';
          const sections = formData.sections;
          const secIdx = Math.min(editSectionTab, Math.max(0, sections.length - 1));
          const section = sections[secIdx];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ThemeProvider theme={ltrTheme}>
                  <Tabs
                    value={sections.length > 0 ? secIdx : false}
                    onChange={(_, v) => setEditSectionTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    {sections.map((s, i) => (
                      <Tab key={i} label={getSectionLabel(s, formData.summary_type_id, i)} sx={{ maxWidth: 200, minWidth: 80 }} />
                    ))}
                  </Tabs>
                </ThemeProvider>
                {!isMeetingSummary && (
                  <Button startIcon={<AddIcon />} onClick={() => { handleAddSection(); setEditSectionTab(formData.sections.length); }} size="small" sx={{ flexShrink: 0 }}>Add</Button>
                )}
              </Box>
              {sections.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isMeetingSummary ? 'Select a summary type on the Settings tab to populate sections.' : 'No sections yet. Click "Add" to add one.'}
                </Typography>
              )}
              {section && (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                  {!isMeetingSummary && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Section Title"
                        value={section.title}
                        onChange={(e) => handleSectionChange(secIdx, 'title', e.target.value)}
                        InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        sx={{ flexGrow: 1, mr: 1 }}
                      />
                      <IconButton size="small" onClick={() => { handleRemoveSection(secIdx); setEditSectionTab(Math.max(0, secIdx - 1)); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Section Prompt"
                    value={section.prompt}
                    onChange={(e) => handleSectionChange(secIdx, 'prompt', e.target.value)}
                    multiline
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
                  />
                </Paper>
              )}
            </Box>
          );
        })()}

        {editTab === 3 && getCriteriaEntity(selectedPrompt?.prompt_type ?? '') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <CriteriaEditorPanel
              key={`edit-include-${editDialogOpen}`}
              label="Include Criteria"
              emptyMessage="No include rules — all records match."
              value={formData.criteria.include}
              onChange={(items) => setFormData((p) => ({ ...p, criteria: { ...p.criteria, include: items } }))}
              entity={getCriteriaEntity(selectedPrompt!.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
            <CriteriaEditorPanel
              key={`edit-exclude-${editDialogOpen}`}
              label="Exclude Criteria"
              emptyMessage="No exclude rules — no records are excluded."
              value={formData.criteria.exclude}
              onChange={(items) => setFormData((p) => ({ ...p, criteria: { ...p.criteria, exclude: items } }))}
              entity={getCriteriaEntity(selectedPrompt!.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
          </Box>
        )}
        </Box>
      </ClinicDialog>

      {/* Copy to Tenant Dialog */}
      <ClinicDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        title="Create Tenant-Specific Copy"
        maxWidth="md"
        fullHeight
        actions={
          <>
            <Button onClick={handleSaveCopy} variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              Create Copy
            </Button>
            <Button onClick={() => setCopyDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>
              Cancel
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Tabs value={copyTab} onChange={(_, v) => setCopyTab(v)} sx={{ mb: 2 }}>
          <Tab label="Settings" />
          <Tab label="Prompt" />
          <Tab label="Sections" />
          {getCriteriaEntity(selectedPrompt?.prompt_type ?? '') && <Tab label="Criteria" />}
        </Tabs>

        {copyTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField fullWidth size="small" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
            </Box>
            {selectedPrompt?.prompt_type === 'meeting_summary' && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Summary Type</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.summary_type_id ?? ''} onChange={(e) => handleSummaryTypeSelect((e.target.value || null) as number | null, false)} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {summaryTypes.map((st) => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Deployment</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.deployment} onChange={(e) => setFormData({ ...formData, deployment: e.target.value as PromptDeployment })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value="dev">Dev</MenuItem>
                    <MenuItem value="pilot">Pilot</MenuItem>
                    <MenuItem value="prod">Prod</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1, minWidth: 80 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Priority</Typography>
                <TextField fullWidth size="small" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Provider</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.model_provider || ''} onChange={(e) => setFormData({ ...formData, model_provider: (e.target.value || null) as PromptProvider | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 2, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Model</Typography>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={MODEL_OPTIONS[formData.model_provider || ''] || []}
                  value={formData.model || ''}
                  onInputChange={(_, value) => setFormData({ ...formData, model: value || null })}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="e.g. gpt-4o, claude-opus-4-7" InputProps={{ ...params.InputProps, style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Thinking Level</Typography>
                <FormControl fullWidth size="small">
                  <Select value={formData.thinking_level || ''} onChange={(e) => setFormData({ ...formData, thinking_level: (e.target.value || null) as PromptThinkingLevel | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        )}

        {copyTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, minHeight: 0 }}>
            {availableVariables.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Click to insert variable at cursor:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableVariables.map((v) => (
                    <Chip key={v.name} label={`{${v.name}}`} size="small" variant="outlined" onClick={() => insertVariable(v.name, false)} sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              value={formData.general}
              onChange={(e) => setFormData({ ...formData, general: e.target.value })}
              multiline
              placeholder="Enter the general prompt text..."
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties, inputRef: generalTextRef }}
              sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
            />
          </Box>
        )}

        {copyTab === 2 && (() => {
          const isMeetingSummary = selectedPrompt?.prompt_type === 'meeting_summary';
          const sections = formData.sections;
          const secIdx = Math.min(copySectionTab, Math.max(0, sections.length - 1));
          const section = sections[secIdx];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ThemeProvider theme={ltrTheme}>
                  <Tabs
                    value={sections.length > 0 ? secIdx : false}
                    onChange={(_, v) => setCopySectionTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    {sections.map((s, i) => (
                      <Tab key={i} label={getSectionLabel(s, formData.summary_type_id, i)} sx={{ maxWidth: 200, minWidth: 80 }} />
                    ))}
                  </Tabs>
                </ThemeProvider>
                {!isMeetingSummary && (
                  <Button startIcon={<AddIcon />} onClick={() => { handleAddSection(); setCopySectionTab(formData.sections.length); }} size="small" sx={{ flexShrink: 0 }}>Add</Button>
                )}
              </Box>
              {sections.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isMeetingSummary ? 'Select a summary type on the Settings tab to populate sections.' : 'No sections yet. Click "Add" to add one.'}
                </Typography>
              )}
              {section && (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                  {!isMeetingSummary && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Section Title"
                        value={section.title}
                        onChange={(e) => handleSectionChange(secIdx, 'title', e.target.value)}
                        InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        sx={{ flexGrow: 1, mr: 1 }}
                      />
                      <IconButton size="small" onClick={() => { handleRemoveSection(secIdx); setCopySectionTab(Math.max(0, secIdx - 1)); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Section Prompt"
                    value={section.prompt}
                    onChange={(e) => handleSectionChange(secIdx, 'prompt', e.target.value)}
                    multiline
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
                  />
                </Paper>
              )}
            </Box>
          );
        })()}

        {copyTab === 3 && getCriteriaEntity(selectedPrompt?.prompt_type ?? '') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <CriteriaEditorPanel
              key={`copy-include-${copyDialogOpen}`}
              label="Include Criteria"
              emptyMessage="No include rules — all records match."
              value={formData.criteria.include}
              onChange={(items) => setFormData((p) => ({ ...p, criteria: { ...p.criteria, include: items } }))}
              entity={getCriteriaEntity(selectedPrompt!.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
            <CriteriaEditorPanel
              key={`copy-exclude-${copyDialogOpen}`}
              label="Exclude Criteria"
              emptyMessage="No exclude rules — no records are excluded."
              value={formData.criteria.exclude}
              onChange={(items) => setFormData((p) => ({ ...p, criteria: { ...p.criteria, exclude: items } }))}
              entity={getCriteriaEntity(selectedPrompt!.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
          </Box>
        )}
        </Box>
      </ClinicDialog>

      {/* Add Prompt Dialog */}
      <ClinicDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title="Add Prompt"
        maxWidth="md"
        fullHeight
        actions={
          <>
            <Button onClick={handleAddPrompt} variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              Create
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>
              Cancel
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={{ mb: 2 }}>
          <Tab label="Settings" />
          <Tab label="Prompt" />
          <Tab label="Sections" />
          {getCriteriaEntity(addFormData.prompt_type) && <Tab label="Criteria" />}
        </Tabs>

        {addTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField fullWidth size="small" value={addFormData.name} onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
            </Box>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Prompt Type</Typography>
              <FormControl fullWidth size="small">
                <Select value={addFormData.prompt_type} onChange={(e) => { setAddFormData({ ...addFormData, prompt_type: e.target.value, meeting_type_id: '', sections: [] }); loadVariables(e.target.value); }} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                  <MenuItem value="" disabled><em>Select type...</em></MenuItem>
                  <MenuItem value="meeting_summary">Meeting Summary</MenuItem>
                  <MenuItem value="questionnaire_summary">Questionnaire Summary</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {addFormData.prompt_type === 'meeting_summary' && (
              <>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Meeting Type</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={addFormData.meeting_type_id} onChange={(e) => handleMeetingTypeSelect(e.target.value as number | '')} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {meetingTypes.map((mt) => (<MenuItem key={mt.id} value={mt.id}>{mt.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Summary Type</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={addFormData.summary_type_id ?? ''} onChange={(e) => handleSummaryTypeSelect((e.target.value || null) as number | null, true)} displayEmpty style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {summaryTypes.map((st) => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Deployment</Typography>
                <FormControl fullWidth size="small">
                  <Select value={addFormData.deployment} onChange={(e) => setAddFormData({ ...addFormData, deployment: e.target.value as PromptDeployment })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value="dev">Dev</MenuItem>
                    <MenuItem value="pilot">Pilot</MenuItem>
                    <MenuItem value="prod">Prod</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1, minWidth: 80 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Priority</Typography>
                <TextField fullWidth size="small" type="number" value={addFormData.priority} onChange={(e) => setAddFormData({ ...addFormData, priority: parseInt(e.target.value) || 1 })} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Provider</Typography>
                <FormControl fullWidth size="small">
                  <Select value={addFormData.model_provider || ''} onChange={(e) => setAddFormData({ ...addFormData, model_provider: (e.target.value || null) as PromptProvider | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 2, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Model</Typography>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={MODEL_OPTIONS[addFormData.model_provider || ''] || []}
                  value={addFormData.model || ''}
                  onInputChange={(_, value) => setAddFormData({ ...addFormData, model: value || null })}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="e.g. gpt-4o, claude-opus-4-7" InputProps={{ ...params.InputProps, style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Thinking Level</Typography>
                <FormControl fullWidth size="small">
                  <Select value={addFormData.thinking_level || ''} onChange={(e) => setAddFormData({ ...addFormData, thinking_level: (e.target.value || null) as PromptThinkingLevel | null })} style={CLINIC_DIALOG_SX.textField as React.CSSProperties}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        )}

        {addTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, minHeight: 0 }}>
            {availableVariables.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Click to insert variable at cursor:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableVariables.map((v) => (
                    <Chip key={v.name} label={`{${v.name}}`} size="small" variant="outlined" onClick={() => insertVariable(v.name, true)} sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}
            {availableVariables.length === 0 && addFormData.prompt_type === '' && (
              <Typography variant="caption" color="text.secondary">Select a prompt type on the Settings tab to see available variables.</Typography>
            )}
            <TextField
              fullWidth
              size="small"
              value={addFormData.general}
              onChange={(e) => setAddFormData({ ...addFormData, general: e.target.value })}
              multiline
              placeholder="Enter the general prompt text..."
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties, inputRef: generalTextRef }}
              sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
            />
          </Box>
        )}

        {addTab === 2 && (() => {
          const isMeetingSummary = addFormData.prompt_type === 'meeting_summary';
          const sections = addFormData.sections;
          const secIdx = Math.min(addSectionTab, Math.max(0, sections.length - 1));
          const section = sections[secIdx];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ThemeProvider theme={ltrTheme}>
                  <Tabs
                    value={sections.length > 0 ? secIdx : false}
                    onChange={(_, v) => setAddSectionTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    {sections.map((s, i) => (
                      <Tab key={i} label={getSectionLabel(s, addFormData.summary_type_id, i)} sx={{ maxWidth: 200, minWidth: 80 }} />
                    ))}
                  </Tabs>
                </ThemeProvider>
                {!isMeetingSummary && (
                  <Button startIcon={<AddIcon />} onClick={() => { handleAddSectionForAdd(); setAddSectionTab(addFormData.sections.length); }} size="small" sx={{ flexShrink: 0 }}>Add</Button>
                )}
              </Box>
              {sections.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isMeetingSummary ? 'Select a summary type on the Settings tab to populate sections.' : 'No sections yet. Click "Add" to add one.'}
                </Typography>
              )}
              {section && (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                  {!isMeetingSummary && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Section Title"
                        value={section.title}
                        onChange={(e) => handleSectionChangeForAdd(secIdx, 'title', e.target.value)}
                        InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        sx={{ flexGrow: 1, mr: 1 }}
                      />
                      <IconButton size="small" onClick={() => { handleRemoveSectionForAdd(secIdx); setAddSectionTab(Math.max(0, secIdx - 1)); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Section Prompt"
                    value={section.prompt}
                    onChange={(e) => handleSectionChangeForAdd(secIdx, 'prompt', e.target.value)}
                    multiline
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' } }}
                  />
                </Paper>
              )}
            </Box>
          );
        })()}

        {addTab === 3 && getCriteriaEntity(addFormData.prompt_type) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <CriteriaEditorPanel
              key={`add-include-${addDialogOpen}`}
              label="Include Criteria"
              emptyMessage="No include rules — all records match."
              value={addFormData.criteria.include}
              onChange={(items) => setAddFormData((p) => ({ ...p, criteria: { ...p.criteria, include: items } }))}
              entity={getCriteriaEntity(addFormData.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
            <CriteriaEditorPanel
              key={`add-exclude-${addDialogOpen}`}
              label="Exclude Criteria"
              emptyMessage="No exclude rules — no records are excluded."
              value={addFormData.criteria.exclude}
              onChange={(items) => setAddFormData((p) => ({ ...p, criteria: { ...p.criteria, exclude: items } }))}
              entity={getCriteriaEntity(addFormData.prompt_type)!}
              dynamicOptions={dynamicOptions}
            />
          </Box>
        )}
        </Box>
      </ClinicDialog>

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} />
    </TablePageLayout>
  </RequirePermission>
  );
};

export default PromptManagementPage;
